import React, { useState, useEffect } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import apiService from '../services/apiService';
import syncService from '../services/syncService';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
    paddingBottom: '100px'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    color: 'white',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  headerTitle: {
    fontSize: '18px',
    fontWeight: '700',
    margin: 0
  },
  backBtn: {
    background: 'rgba(255,255,255,0.2)',
    border: 'none',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  content: {
    padding: '20px'
  },
  infoBox: {
    background: '#ede9fe',
    border: '2px solid #8b5cf6',
    borderRadius: '12px',
    padding: '12px 16px',
    marginBottom: '20px'
  },
  infoText: {
    fontSize: '13px',
    color: '#6b21a8',
    margin: 0
  },
  photoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '20px'
  },
  photoSlot: {
    aspectRatio: '1',
    border: '2px dashed #d1d5db',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    background: '#f9fafb',
    overflow: 'hidden',
    position: 'relative'
  },
  photoSlotFilled: {
    border: '2px solid #8b5cf6'
  },
  photoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  photoLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '600',
    marginTop: '8px',
    textAlign: 'center'
  },
  photoIcon: {
    fontSize: '32px'
  },
  deleteBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '700',
    zIndex: 10
  },
  addPhotosBtn: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  uploadInfo: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '12px'
  },
  photosBadge: {
    background: '#ede9fe',
    color: '#6b21a8',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    display: 'inline-block',
    marginBottom: '16px'
  },
  saveSection: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    padding: '16px 20px',
    background: 'white',
    boxShadow: '0 -4px 16px rgba(0,0,0,0.1)'
  },
  saveButton: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
    border: 'none',
    borderRadius: '12px',
    color: 'white',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer'
  }
};

function Etape3PhotosMachineMobile({ mission, onBack, onSave }) {
  // 🔥 Utiliser un array comme BASE44 au lieu d'un objet
  const [photos, setPhotos] = useState([]);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [syncingPending, setSyncingPending] = useState(false);

  useEffect(() => {
    loadData();
  }, [mission.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const savedData = await inspectionStorage.getStep(mission.id, 'etape_3_photos_machine_levage');
      
      // 🔥 Gérer l'ancien format (objet) et le nouveau (array)
      if (savedData) {
        if (Array.isArray(savedData.photos)) {
          setPhotos(savedData.photos);
        } else if (savedData.photos && typeof savedData.photos === 'object') {
          // Migrer ancien format objet vers array
          const migratedPhotos = Object.entries(savedData.photos)
            .filter(([_, value]) => value)
            .map(([_, value]) => value);
          setPhotos(migratedPhotos);
        }
        if (Array.isArray(savedData.pending_photos)) {
          const normalizedPending = savedData.pending_photos
            .map((item) => {
              if (typeof item === 'string') {
                return { dataUrl: item, name: 'photo-offline.jpg', addedAt: Date.now() };
              }
              if (item && typeof item === 'object' && item.dataUrl) {
                return item;
              }
              return null;
            })
            .filter(Boolean);
          setPendingPhotos(normalizedPending);
        }
      }
    } catch (error) {
      console.error('❌ Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const readFilesAsDataUrls = async (files) => {
    const readers = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve({ dataUrl: event.target.result, name: file.name });
        reader.readAsDataURL(file);
      });
    });
    return await Promise.all(readers);
  };

  const addPendingPhotos = (items) => {
    if (!items || items.length === 0) return;
    const normalized = items.map((item) => ({
      dataUrl: item.dataUrl,
      name: item.name || 'photo-offline.jpg',
      addedAt: Date.now()
    }));
    setPendingPhotos(prev => [...prev, ...normalized]);
  };

  const handlePhotoCapture = (useCamera = true) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true; // 🔥 Permettre sélection multiple
    
    if (useCamera) {
      input.capture = 'environment';
    }
    
    input.onchange = async (e) => {
      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      if (!navigator.onLine) {
        const dataUrls = await readFilesAsDataUrls(files);
        addPendingPhotos(dataUrls);
        alert('Hors ligne : photos sauvegardées localement et envoyées à la synchro.');
        return;
      }

      setUploading(true);
      try {
        const uploadedUrls = [];
        for (const file of files) {
          const result = await apiService.uploadFile(file, 'photo_levage');
          if (!result.success || !result.file_url) {
            throw new Error(result.error || 'Erreur upload photo');
          }
          uploadedUrls.push(result.file_url);
        }
        setPhotos(prev => [...prev, ...uploadedUrls]);
      } catch (error) {
        console.error('❌ Erreur upload photos:', error);
        const dataUrls = await readFilesAsDataUrls(files);
        addPendingPhotos(dataUrls);
        alert('Upload impossible : photos conservées en mode offline.');
      } finally {
        setUploading(false);
      }
    };

    input.click();
  };

  const handleDeletePhoto = (index) => {
    if (window.confirm('Supprimer cette photo ?')) {
      setPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleDeletePendingPhoto = (index) => {
    if (window.confirm('Supprimer cette photo en attente ?')) {
      setPendingPhotos(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await inspectionStorage.saveStep(mission.id, 'etape_3_photos_machine_levage', {
        photos,
        pending_photos: pendingPhotos
      });
      console.log('✅ Étape 3 levage sauvegardée');
      if (onSave) onSave({ photos, pending_photos: pendingPhotos });
      onBack();
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncPending = async () => {
    if (!navigator.onLine) {
      alert('Vous êtes hors ligne.');
      return;
    }
    setSyncingPending(true);
    try {
      const result = await syncService.uploadMission(mission.id);
      if (result?.success) {
        const refreshed = await inspectionStorage.getStep(mission.id, 'etape_3_photos_machine_levage');
        if (refreshed?.photos) {
          setPhotos(refreshed.photos);
        }
        if (Array.isArray(refreshed?.pending_photos)) {
          setPendingPhotos(refreshed.pending_photos);
        } else {
          setPendingPhotos([]);
        }
        alert('Photos en attente envoyées.');
      } else {
        alert('Envoi impossible pour le moment.');
      }
    } catch (error) {
      console.error('❌ Erreur sync pending photos:', error);
      alert('Erreur lors de l\'envoi des photos.');
    } finally {
      setSyncingPending(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#fffbeb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e5e7eb', borderTopColor: '#8b5cf6', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }}></div>
          <p style={{ marginTop: '16px', color: '#6b21a8', fontWeight: '500' }}>Chargement...</p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={onBack}>← Retour</button>
        <h1 style={styles.headerTitle}>Photos machine</h1>
        <button style={styles.backBtn} onClick={handleSave} disabled={saving || uploading}>
          {saving ? '⏳' : '💾'}
        </button>
      </div>

      <div style={styles.content}>
        {/* Info */}
        <div style={styles.infoBox}>
          <p style={styles.infoText}>
            📸 Ajoutez des photos de la machine vérifiée
          </p>
        </div>

        {/* Badge nombre de photos */}
        <div style={styles.photosBadge}>
          {photos.length} photo{photos.length > 1 ? 's' : ''} ajoutée{photos.length > 1 ? 's' : ''}
          {pendingPhotos.length > 0 && ` · ${pendingPhotos.length} en attente`}
        </div>

        {uploading && (
          <div style={styles.uploadInfo}>⏳ Upload des photos en cours...</div>
        )}

        {/* Boutons ajout */}
        <button
          style={styles.addPhotosBtn}
          onClick={() => handlePhotoCapture(true)}
          disabled={uploading}
        >
          📷 Prendre une photo
        </button>

        <button 
          style={{ ...styles.addPhotosBtn, background: 'linear-gradient(135deg, #6366f1, #4f46e5)' }} 
          onClick={() => handlePhotoCapture(false)}
          disabled={uploading}
        >
          🖼️ Choisir depuis la galerie
        </button>

        {pendingPhotos.length > 0 && (
          <button
            style={{ ...styles.addPhotosBtn, background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}
            onClick={handleSyncPending}
            disabled={uploading || syncingPending}
          >
            {syncingPending ? '⏳ Envoi des photos...' : '☁️ Envoyer les photos en attente'}
          </button>
        )}

        {/* Grille photos */}
        {photos.length === 0 && pendingPhotos.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            background: 'white',
            borderRadius: '16px',
            border: '2px dashed #d1d5db'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📷</div>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
              Aucune photo
            </p>
            <p style={{ fontSize: '13px', color: '#9ca3af' }}>
              Appuyez sur les boutons ci-dessus pour ajouter des photos
            </p>
          </div>
        ) : (
          <div style={styles.photoGrid}>
            {photos.map((photo, index) => (
              <div key={index}>
                <div style={{ ...styles.photoSlot, ...styles.photoSlotFilled }}>
                  <img src={photo} style={styles.photoImage} alt={`Photo ${index + 1}`} />
                  <button
                    style={styles.deleteBtn}
                    onClick={() => handleDeletePhoto(index)}
                  >
                    ✕
                  </button>
                </div>
                <p style={styles.photoLabel}>Photo #{index + 1}</p>
              </div>
            ))}
            {pendingPhotos.map((photo, index) => (
              <div key={`pending-${index}`}>
                <div style={{ ...styles.photoSlot, ...styles.photoSlotFilled, border: '2px dashed #f59e0b' }}>
                  <img src={photo.dataUrl} style={styles.photoImage} alt={`Photo en attente ${index + 1}`} />
                  <button
                    style={{ ...styles.deleteBtn, background: '#f59e0b' }}
                    onClick={() => handleDeletePendingPhoto(index)}
                  >
                    ✕
                  </button>
                </div>
                <p style={styles.photoLabel}>En attente #{index + 1}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Bouton sauvegarder */}
      <div style={styles.saveSection}>
        <button 
          style={{ ...styles.saveButton, ...((saving || uploading) ? { opacity: 0.6, cursor: 'not-allowed' } : {}) }} 
          onClick={handleSave} 
          disabled={saving || uploading}
        >
          {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
        </button>
      </div>
    </div>
  );
}

export default Etape3PhotosMachineMobile;